import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableRow, TableCell, Typography, Button, TableBody, Stack } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { DynamicIconButton } from '../../utils/tooltip/DynamicIconButton'
import { formatPercentage } from '../../packages/utils/numberFormatters'
import { calculateDiscount } from './utils'
import { IssueEntity } from '../../types/IssueEntity'

const Row = ({ label, shareDiscount, terpDiscount, includeTerpInTable }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    return (
        <TableRow>
            <TableCell sx={{ paddingLeft: '32px' }}>{label}</TableCell>
            <TableCell align='right'>
                {shareDiscount > 0 ? '+' : ''}
                {shareDiscount && shareDiscount !== 0 ? formatPercentage(shareDiscount, { locale, decimals: 0 }) : '-'}
            </TableCell>
            {includeTerpInTable ? (
                <TableCell align='right'>
                    {terpDiscount > 0 ? '+' : ''}
                    {terpDiscount && terpDiscount !== 0 ? formatPercentage(terpDiscount, { locale, decimals: 0 }) : '-'}
                </TableCell>
            ) : null}
        </TableRow>
    )
}

const getFormattedVwapSharePrice = (sharePrice, locale, t) => {
    if (isNaN(sharePrice)) return t('pages.issues.discount.notAvailable')

    return (
        formatPercentage(sharePrice, {
            locale,
            decimals: 0,
            prefix: sharePrice > 0 ? '+' : '',
        }) +
        ' ' +
        t('pages.issues.discount.vsSharePrice')
    )
}

function IssueTermsDiscountRow({ issue }: { issue: IssueEntity }) {
    const [extended, setExtended] = useState(false)
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    if (issue.sharesInUnit === 0 && issue.type === 'Rights issue') {
        return null
    }

    if (!issue.unitSubPrice) return null

    const pub10VwapStockPrice =
        issue.tradesStats.find(({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays === 10)
            ?.vwap || null
    const terms10VwapStockPrice =
        issue.tradesStats.find(({ periodType, numberOfDays }) => periodType === 'preTerms' && numberOfDays === 10)
            ?.vwap || null

    const default10VwapValue = issue.noTermsInitially || issue.termsDate ? terms10VwapStockPrice : pub10VwapStockPrice

    const default10VwapSharePrice = calculateDiscount(issue, default10VwapValue, false)
    const default10VwapTerp = calculateDiscount(issue, default10VwapValue, true)
    const formattedVwapSharePrice = getFormattedVwapSharePrice(default10VwapSharePrice, locale, t)

    const showDetailedTable = issue.tradesStats.some(
        (tradeStat) => ['prePublication', 'preTerms'].includes(tradeStat.periodType) && tradeStat.vwap !== null,
    )

    const discount10VwapExists = default10VwapSharePrice !== null || default10VwapTerp !== null

    const includeTerpInTable = issue.type === 'Rights issue'

    return (
        <TableRow
            sx={{
                '&:last-child th': {
                    border: 0,
                },
            }}
        >
            <TableCell
                sx={{
                    verticalAlign: 'top',
                    width: '50%',
                    paddingBottom: '16px',
                }}
            >
                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Typography>
                        {extended ? t('pages.issues.discount.label') : t('pages.issues.discount.labelWithVwap')}
                    </Typography>
                    {includeTerpInTable && (
                        <DynamicIconButton
                            title={
                                <>
                                    <p>{t('pages.issues.discount.tooltipIntro')}</p>
                                    <p>{t('pages.issues.discount.tooltipTerp')}</p>
                                    <p>{t('pages.issues.discount.tooltipFormula')}</p>
                                </>
                            }
                            IconComponent={InfoIcon}
                            placement='top'
                            padding='0px 4px'
                        />
                    )}
                </Stack>

                {showDetailedTable ? (
                    <Button onClick={() => setExtended(!extended)} sx={{ padding: 0, justifyContent: 'left' }}>
                        {extended ? t('site.actions.hideTable') : t('site.actions.showTable')}
                    </Button>
                ) : null}
            </TableCell>
            {extended ? (
                <TableCell
                    colSpan={2}
                    sx={{
                        paddingRight: '0px',
                    }}
                >
                    <Table size='small' sx={{ maxWidth: '500px' }}>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <strong>{t('pages.issues.discount.timePeriod')}</strong>
                                </TableCell>
                                <TableCell align='right'>
                                    <strong>
                                        {includeTerpInTable
                                            ? t('pages.issues.discount.sharePrice')
                                            : t('pages.issues.discount.discount')}
                                    </strong>
                                </TableCell>
                                {includeTerpInTable ? (
                                    <TableCell align='right'>
                                        <strong>{t('pages.issues.discount.terp')}</strong>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                            {issue.noTermsInitially || issue.termsDate ? (
                                <TableRow>
                                    <TableCell>
                                        <i>{t('pages.issues.discount.fromPublication')}</i>
                                    </TableCell>
                                    <TableCell colSpan={includeTerpInTable ? 2 : 1} />
                                </TableRow>
                            ) : null}
                            {issue.tradesStats
                                .filter(({ periodType }) => periodType === 'prePublication')
                                .map(({ closePrice, vwap, numberOfDays }) => (
                                    <Row
                                        key={numberOfDays}
                                        label={t(
                                            numberOfDays === 1
                                                ? 'pages.issues.discount.oneDayClose'
                                                : 'pages.issues.discount.daysVwap',
                                            { count: numberOfDays },
                                        )}
                                        shareDiscount={calculateDiscount(
                                            issue,
                                            numberOfDays === 1 ? closePrice : vwap,
                                            false,
                                        )}
                                        terpDiscount={calculateDiscount(
                                            issue,
                                            numberOfDays === 1 ? closePrice : vwap,
                                            true,
                                        )}
                                        includeTerpInTable={includeTerpInTable}
                                    />
                                ))}
                            {issue.noTermsInitially || issue.termsDate ? (
                                <>
                                    <TableRow>
                                        <TableCell>
                                            <i>{t('pages.issues.discount.fromTermsDate')}</i>
                                        </TableCell>
                                        <TableCell colSpan={includeTerpInTable ? 2 : 1} />
                                    </TableRow>

                                    {issue.tradesStats
                                        .filter(({ periodType }) => periodType === 'preTerms')
                                        .map(({ closePrice, vwap, numberOfDays }) => (
                                            <Row
                                                key={numberOfDays}
                                                label={t(
                                                    numberOfDays === 1
                                                        ? 'pages.issues.discount.oneDayClose'
                                                        : 'pages.issues.discount.daysVwap',
                                                    { count: numberOfDays },
                                                )}
                                                shareDiscount={calculateDiscount(
                                                    issue,
                                                    numberOfDays === 1 ? closePrice : vwap,
                                                    false,
                                                )}
                                                terpDiscount={calculateDiscount(
                                                    issue,
                                                    numberOfDays === 1 ? closePrice : vwap,
                                                    true,
                                                )}
                                                includeTerpInTable={includeTerpInTable}
                                            />
                                        ))}
                                </>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableCell>
            ) : (
                <TableCell
                    sx={{
                        width: '50%',
                        paddingBottom: '16px',
                    }}
                >
                    {discount10VwapExists ? (
                        <>
                            <Typography>{formattedVwapSharePrice}</Typography>
                            {includeTerpInTable ? (
                                <Typography>
                                    {default10VwapTerp && default10VwapTerp > 0 ? '+' : ''}
                                    {formatPercentage(default10VwapTerp, {
                                        locale,
                                        decimals: 0,
                                    })}{' '}
                                    {t('pages.issues.discount.vsTerp')}
                                </Typography>
                            ) : null}
                        </>
                    ) : (
                        <Typography>{t('pages.issues.discount.cannotCalculate')}</Typography>
                    )}
                </TableCell>
            )}
        </TableRow>
    )
}

export default IssueTermsDiscountRow
